import {
  Instagram,
  Youtube,
  Link as LinkIcon,
  Globe,
  Twitter,
  Facebook,
  Twitch,
  Music2,
  Gift
} from "lucide-react";
import { cn } from "@/lib/utils";

const AppleLogo = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 384 512"
    className={cn("fill-current", className)}
    width="24"
    height="24"
    stroke="none"
  >
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 69.8c23.5 50.7 35.8 55.4 54.6 91.5 13.4 25.4 29.5 26.8 53.6 26.8 23 0 35.8-19.5 73.4-19.5 37.1 0 46.1 19.5 69.3 19.5 27.4 0 45.4-23.7 66-54.7 9.4-16.7 20-37.5 28.5-63-48-20.9-85.1-60.8-85.1-135.2zm-58.4-191.7c18.5-25.5 35.5-56.1 30.2-86.8-26.6 2.7-56 16.7-74.4 39.1-15.6 18.2-27.1 48-23.5 81.6 27.2 2.1 53-15.1 67.7-33.9z" />
  </svg>
);

export const ICON_LIBRARY: Record<string, { icon: React.ComponentType<{ className?: string }>; label: string }> = {
  instagram: { icon: Instagram, label: "Instagram" },
  youtube: { icon: Youtube, label: "YouTube" },
  tiktok: { icon: Music2, label: "TikTok" },
  apple: { icon: AppleLogo, label: "Apple" },
  twitter: { icon: Twitter, label: "Twitter/X" },
  facebook: { icon: Facebook, label: "Facebook" },
  twitch: { icon: Twitch, label: "Twitch" },
  link: { icon: LinkIcon, label: "Link" },
  google: { icon: Globe, label: "Google" },
  default: { icon: Gift, label: "Default" },
};

interface EntryMethodIconProps {
  iconType?: string | null;
  iconKey?: string | null;
  iconUrl?: string | null;
  className?: string;
  size?: "sm" | "md" | "lg";
}

export function EntryMethodIcon({
  iconType,
  iconKey,
  iconUrl,
  className,
  size = "md"
}: EntryMethodIconProps) {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };

  // Priority 1: If iconUrl exists and is non-empty, render uploaded image
  if (iconUrl && iconUrl.trim() !== "") {
    return (
      <img
        src={iconUrl}
        alt="Entry method icon"
        className={cn(sizeClasses[size], "object-contain", className)}
        onError={(e) => {
          // Fallback to default icon if image fails to load
          e.currentTarget.style.display = "none";
          const parent = e.currentTarget.parentElement;
          if (parent) {
            parent.innerHTML = "";
            // We can't render React here, so just use a simple SVG fallback
            const DefaultIcon = ICON_LIBRARY.default.icon;
            // This is a workaround - the parent component should handle this
          }
        }}
      />
    );
  }

  // Priority 2: If iconKey exists and is valid in library, render library icon
  if (iconKey && iconKey.trim() !== "" && ICON_LIBRARY[iconKey]) {
    const IconComponent = ICON_LIBRARY[iconKey].icon;
    return <IconComponent className={cn(sizeClasses[size], className)} />;
  }

  // Priority 3: Default fallback icon
  const DefaultIcon = ICON_LIBRARY.default.icon;
  return <DefaultIcon className={cn(sizeClasses[size], className)} />;
}
